import { Component, computed, effect, inject } from '@angular/core';
import { CustomRouteDesktopComponent } from './components/custom-route-desktop/custom-route-desktop.component';
import { CustomRouteService } from './services/custom-route.service';
import { Point } from './interfaces/custom-route.interface';
import { catchError, of } from 'rxjs';
import { CustomRouteResponseCompleted } from './interfaces/custom-route.response';
import { RegisterUserService } from '../register-user/services/register-user.service';
import { PlannerDayStateService } from './shared/planner/services';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Box1Component } from "src/app/fitur-26/layout/boxes";
import { Card1Component } from "src/app/fitur-26/layout/cards";
import { WebsocketLaravelReverb } from 'src/app/fitur-26/pages/home/services';
import { CHANNEL_ATRIL_PANTALLA_NAME, GO_TO_NORMAL_OR_SATURED_DETAIL_PAGE_EVENT } from 'src/app/fitur-26/pages/home/config';
import { TranslationService } from 'src/app/services';
import { customRouteAdapter } from './adapters/custom-route.adapter';

@Component({
  selector: 'app-custom-route',
  standalone: true,
  imports: [
    TranslateModule,
    CustomRouteDesktopComponent,
    Box1Component,
    Card1Component
],
  templateUrl: './custom-route.component.html',
  styleUrls: ['./custom-route.component.scss'],
  host: {
    style: 'display: block; height: 100%; width: 100%;'
  }
})
export class CustomRouteComponent {
  private _router = inject(Router);
  private _activedRoute = inject(ActivatedRoute);
  private _customRouteService = inject(CustomRouteService);
  private _registerUserService = inject(RegisterUserService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _websocketLaravelReverbService = inject(WebsocketLaravelReverb);
  private _translationService = inject(TranslationService);

  protected customRouteData =  this._customRouteService.customRouteData;
  private _routeToken =  this._customRouteService.token;
  protected dayToShowIndex = this._plannerDayStateService.dayToShowIndex;
  protected isLoading = this._customRouteService.isLoading;
  
  protected saturatedPointAndRouteIndexList = computed(() => {
    const dailyPoints = this.customRouteData()?.dailyRoutes[this.dayToShowIndex()]?.points;
    if (!dailyPoints) return [];

    // Devuelve un array de objetos { point, index }
    return dailyPoints
      .map((point, index) => ({ point, index }))
      .filter(({ point }) => {
        const relatedItems = point.relatedsItems;
        return relatedItems && relatedItems.length > 0;
      });
  });
 
  constructor() {
    this._activedRoute.paramMap.subscribe(params => {
      const routeTokenValue = params.get('routeToken');
      if (routeTokenValue) {
        this._loadCustomRouteFromRouteToken(routeTokenValue);
      }
    });
  }
  
  // Effects
  setNotSaturedPointFlag = effect(() => {
    if (this.saturatedPointAndRouteIndexList().length === 0) {
      this._customRouteService.setNoSaturedPoints(true);
    } else {
      this._customRouteService.setNoSaturedPoints(false);
    }
  }, { allowSignalWrites: true });

  /**
   * Carga la ruta personalizada desde el token de la ruta.
   * Para rutas cargadas desde el email, se espera que el token esté en la URL
   */
  private _loadCustomRouteFromRouteToken(routeToken: string) {
    this._customRouteService.getCustomRouteResponse(routeToken)
      .pipe(
        catchError((_error) => {
          this._router.navigateByUrl('/home');
          return of(null); 
        })
      )
      .subscribe((res: any) => {
        this._customRouteService.setToken(res.token);
        
        this._customRouteService.setCurrentOrder(res.dailyRoutes?.length);
        
        this._registerUserService.setNumberOfDaysCustomRoute(res.dailyRoutes?.length);
        
        this._translationService.setTranslateService(res.language || 'es');

        // add all the daily routes to the customRouteData
        res.dailyRoutes?.forEach((_, index) => {
          const args: CustomRouteResponseCompleted = {
            status: 200,
            id: res.id,
            token: res.token,
            language: res.language,
            dailyRoutes: res.dailyRoutes[index]
          }
          this._customRouteService.addDailyRoute(args); 
        })
    
        const customRoute = customRouteAdapter(
          res.id,
          res.token,
          res.language,
          res.dailyRoutes
        );
        
        this._customRouteService.setCustomRouteData(customRoute);
        this._customRouteService.setRouteUpdated(true);
      }
    )
  }
  
  protected goToDetailPage(pointId: number, pointType: string, from: string): void {
    const typeNormalized = pointType === 'event' ? 'event' : 'point';
    
    this._websocketLaravelReverbService
      .sendMessage(
        CHANNEL_ATRIL_PANTALLA_NAME,
        GO_TO_NORMAL_OR_SATURED_DETAIL_PAGE_EVENT + this._routeToken(),
        { type: typeNormalized, id: pointId }
      )
    
    this._router.navigate(
      ['/home', typeNormalized, pointId], 
      { queryParams: { from } }
    );
  }
  
  protected checkIsSaturated(saturedPoint: Point): boolean {
    return !saturedPoint.categoryList.some(category => category.id === 10);
  }
  
  protected checkIsVerySaturated(saturedPoint: Point): boolean {
    return saturedPoint.categoryList.some(category => category.id === 10);
  }  
}
